import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getInsight, insights } from "../content/insights";
import { useLocale, useLocalePath, useT } from "../i18n/useT";

export default function InsightDetail() {
  const t = useT();
  const localePath = useLocalePath();
  const locale = useLocale();
  const dateFmt = locale === "es" ? "es-ES" : "en-GB";
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getInsight(slug) : undefined;

  useEffect(() => {
    if (post) document.title = `${post.title} · SV4-tech`;
    return () => {
      document.title = "SV4-tech | Data Platform & Software Architecture Consultancy";
    };
  }, [post]);

  if (!post) return <Navigate to={localePath("/insights")} replace />;

  const idx = insights.findIndex((p) => p.slug === post.slug);
  const next = insights[idx + 1];

  return (
    <div className="min-h-screen bg-ink-950 text-ink-100">
      <Header />
      <main className="container-page py-16 sm:py-24">
        <Link
          to={localePath("/insights")}
          className="inline-flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-100"
        >
          <ArrowLeft className="size-4" />
          {t.insightDetail.backToInsights}
        </Link>

        <article className="mt-8 max-w-prose">
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-400">
            <span className="font-mono uppercase tracking-wider text-accent-500">
              {post.category}
            </span>
            <span>·</span>
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString(dateFmt, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </time>
            <span>·</span>
            <span>
              {post.readMinutes} {t.insightDetail.minRead}
            </span>
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-50 leading-tight">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-ink-300 leading-relaxed">{post.excerpt}</p>

          <div className="mt-10 space-y-6 text-base text-ink-200 leading-relaxed">
            {post.body.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </article>

        {next && (
          <aside className="mt-16 max-w-prose border-t border-ink-800/80 pt-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink-400">
              {t.insightDetail.next}
            </p>
            <Link to={localePath(`/insights/${next.slug}`)} className="group mt-3 block">
              <h3 className="text-lg font-semibold text-ink-50 group-hover:text-accent-400 transition">
                {next.title}
              </h3>
              <span className="mt-2 inline-flex items-center gap-1.5 text-sm text-accent-400 group-hover:text-accent-500">
                {t.insightDetail.read}
                <ArrowRight className="size-4" />
              </span>
            </Link>
          </aside>
        )}
      </main>
      <Footer />
    </div>
  );
}
